import { useCallback, useEffect, useState } from 'react';
import { Box, Button, Chip, Skeleton, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ArrowForward } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../constants/routes';
import { useAuth } from '../../auth/useAuth';
import { getAllTests, type TestListingRow } from '../../services/testListingService';
import { isValidationRow } from '../tests/testsPageUtils';
import { MyTestsList } from '../tests/MyTestsList';

const PREVIEW_LIMIT = 6;

/**
 * Phone/iPad dashboard (mockups 2026-07-24, Option N1): instead of the
 * desktop stat grids, the mobile home screen is a work surface — the most
 * recently touched active tests, one tap from the test entry page. The full
 * filterable list lives on My Tests (bottom nav).
 */
export const MobileDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rows, setRows] = useState<TestListingRow[] | null>(null);

  useEffect(() => {
    getAllTests({ status: 'New,In Progress,Request Review' })
      .then((data) => setRows(
        data
          .filter((r) => !isValidationRow(r))
          .sort((a, b) => String(b.sysLastModifiedDate).localeCompare(String(a.sysLastModifiedDate)))
          .slice(0, PREVIEW_LIMIT),
      ))
      .catch(() => setRows([]));
  }, []);

  const openTest = useCallback((row: TestListingRow) => {
    if (row.ProjectId && row.WorkOrderKey && row.WorkOrderDetailKey) {
      navigate(ROUTES.TEST_PARAMETERS.replace(':projectId', row.ProjectId)
        .replace(':workOrderId', row.WorkOrderKey)
        .replace(':workOrderDetailKey', row.WorkOrderDetailKey));
    }
  }, [navigate]);

  const firstName = user?.displayName?.split(' ')[0] || 'there';

  return (
    <Box sx={{ pb: 1 }}>
      <Typography sx={{ fontWeight: 700, fontSize: '1.2rem', lineHeight: 1.2 }}>
        Welcome back, {firstName}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 2 }}>
        Geotechnical Laboratory Management
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Chip label="My tests · in progress" size="small"
          sx={{
            height: 22, fontSize: '0.68rem', fontWeight: 700,
            backgroundColor: alpha('#4A6741', 0.1), color: '#4A6741',
          }} />
        <Button
          size="small" endIcon={<ArrowForward sx={{ fontSize: 14 }} />}
          onClick={() => navigate(ROUTES.MY_TESTS)}
          sx={{ ml: 'auto', textTransform: 'none', fontSize: '0.72rem', fontWeight: 600 }}
        >
          All my tests
        </Button>
      </Box>

      {rows === null ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} variant="rounded" height={62} sx={{ borderRadius: '10px' }} />)}
        </Box>
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          No active tests right now.
        </Typography>
      ) : (
        <MyTestsList rows={rows} onOpen={openTest} grouped={false} />
      )}
    </Box>
  );
};
